import { TrashSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Text } from '~/components/ui/text';
import { useTenantSlug } from '~/features/tenant/helpers';
import { FinalReportService } from '~/services/api/final-report/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { RequestErrorBanner } from '~/services/api/rest-api-error';

interface DeleteTemplateDialogProps {
  templateName: string;
  templateID: number | undefined;
  inEdit?: boolean;
}

const DeleteTemplateDialog = ({
  templateID,
  templateName,
  inEdit,
}: DeleteTemplateDialogProps) => {
  const [openDeleteDialog, setOpenDelete] = useState(false);

  const handleCloseDeleteDialog = () => {
    setOpenDelete(false);
  };

  const slug = useTenantSlug();
  const removeTemplate = useWrappedMutation(
    FinalReportService.endpoints.deleteTemplate.useMutation(),
  );

  const deleteTemplate = async () => {
    if (!templateID) {
      return;
    }
    await removeTemplate.mutateAsync(
      { slug, id: templateID },
      {
        onSuccess: () => {
          handleCloseDeleteDialog();
        },
      },
    );
  };
  return (
    <Dialog open={openDeleteDialog} onOpenChange={setOpenDelete}>
      <DialogTrigger asChild>
        <Button
          variant='plain'
          color='danger'
          sx={{
            textTransform: 'none',
          }}
          startDecorator={!inEdit && <TrashSimple />}
          type='button'
        >
          {inEdit ? (
            <FormattedMessage defaultMessage='Remove template' />
          ) : (
            <FormattedMessage defaultMessage='Remove' />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>
          <FormattedMessage defaultMessage='Delete Report Template Warning' />
        </DialogTitle>
        <DialogContent>
          <RequestErrorBanner error={removeTemplate.state.error} />
          <Text>
            <FormattedMessage
              defaultMessage='Do you wish to delete {template}? This action cannot be undone!'
              values={{
                template: templateName,
              }}
            />
          </Text>
          <DialogActions>
            <Button
              variant='outlined'
              color='neutral'
              onClick={handleCloseDeleteDialog}
            >
              <FormattedMessage defaultMessage='Cancel' />
            </Button>
            <Button
              variant='solid'
              color='danger'
              onClick={deleteTemplate}
              disabled={removeTemplate.state.isLoading}
            >
              <Text>
                <FormattedMessage defaultMessage='Delete Template' />
              </Text>
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTemplateDialog;
